import RNAnalysisQuestions from "./RNAnalysisQuestions";
import {rnCadenceRegions} from "../utils/rnCadenceRegions.js";
import React, {useEffect, useRef, useState} from 'react';
import {OpenSheetMusicDisplay} from 'opensheetmusicdisplay';
import QuestionLayout from './QuestionLayout';
import {encodeRomanNumeralText, plainRomanNumeralText, rnAnchorId, timestampNumber, leadSheetChordOptions} from '../utils/romanNumerals';
import './RomanNumeralAnalysis.css';

// Anchors use logical measure + musical time, independent of line breaks, zoom,
// voice order and which staff was clicked. All simultaneous notes share one box.
export function collectRNAnchors(osmd, host) {
  const anchors = [];
  const hostBox = host.getBoundingClientRect();
  let measureNumber = 0, systemId = 0;
  for (const page of osmd.GraphicSheet?.MusicPages || []) {
    for (const system of page.musicSystems || []) {
      systemId++;
      for (const staves of system.graphicalMeasures || []) {
        measureNumber++;
        const entries = new Map();
        let bottom = 0, top = Infinity;
        for (const measure of staves) {
          if (!measure) continue;
          const bbox = measure.boundingBox;
          const pos = bbox?.absolutePosition;
          const rect = bbox?.boundingRectangle;
          // OSMD uses 10 SVG units per engraving unit at zoom 1.
          const rootSvg = host.querySelector('svg');
          if (pos && rect && rootSvg?.getScreenCTM()) {
            const point = rootSvg.createSVGPoint();
            point.x = pos.x * 10; point.y = (pos.y + rect.y + rect.height) * 10;
            const screen = point.matrixTransform(rootSvg.getScreenCTM());
            bottom = Math.max(bottom, screen.y - hostBox.top);
            point.y = (pos.y + rect.y) * 10;
            top = Math.min(top, point.matrixTransform(rootSvg.getScreenCTM()).y - hostBox.top);
          }
          for (const entry of measure.staffEntries || []) {
            const time = timestampNumber(entry.relInMeasureTimestamp);
            const id = rnAnchorId(measureNumber, time);
            for (const voice of entry.graphicalVoiceEntries || []) {
              for (const note of voice.notes || []) {
                const el = note.vfnote?.[0]?.attrs?.el;
                if (!el) continue;
                const box = el.getBoundingClientRect();
                bottom = Math.max(bottom, box.bottom - hostBox.top);
                top = Math.min(top, box.top - hostBox.top);
                if (!note.sourceNote?.Pitch) continue; // no prompts at rests
                const head = el.querySelector('.vf-notehead') || el;
                const headBox = head.getBoundingClientRect();
                const x = headBox.left + headBox.width / 2 - hostBox.left;
                const y = headBox.top + headBox.height / 2 - hostBox.top;
                const existing = entries.get(id);
                const hit = {x, y, bounds:{left:box.left-hostBox.left,top:box.top-hostBox.top,
                  right:box.right-hostBox.left,bottom:box.bottom-hostBox.top}};
                if (existing) existing.hits.push(hit);
                else entries.set(id, {id, measure: measureNumber, time, x, systemId, hits: [hit]});
              }
            }
          }
        }
        for (const anchor of entries.values()) anchors.push({...anchor, y: bottom + 8, top: Number.isFinite(top) ? top : 76});
      }
    }
  }
  const systemTops = new Map();
  for (const anchor of anchors) systemTops.set(anchor.systemId, Math.min(systemTops.get(anchor.systemId) ?? Infinity, anchor.top));
  return anchors.map(anchor => ({...anchor, aboveY:Math.max(4,systemTops.get(anchor.systemId)-64)}))
    .sort((a,b) => a.measure - b.measure || a.time - b.time);
}

export function RomanNumeralInput({value, onChange, ...props}) {
  const ref = useRef(null);
  function edit(event) {
    const input = event.target;
    // Decode the prefix too: a stacked figure is one character on screen.
    const caret = encodeRomanNumeralText(input.value.slice(0, input.selectionStart)).length;
    const plain = plainRomanNumeralText(input.value);
    onChange(plain);
    input.value = encodeRomanNumeralText(plain);
    input.setSelectionRange(caret, caret);
  }
  return <input {...props} ref={ref} value={encodeRomanNumeralText(value)} onChange={edit}
    autoComplete="off" autoCapitalize="off" spellCheck={false} />;
}

export function ChordSymbolSelect({value = '', onChange, locked, ...props}) {
  return <select {...props} value={value} disabled={locked} onChange={event => onChange(event.target.value)}>
    <option value="">Chord</option>
    {value && !leadSheetChordOptions.includes(value) && <option value={value} disabled>{value}</option>}
    {leadSheetChordOptions.map(chord => <option key={chord} value={chord}>{chord}</option>)}
  </select>;
}

export function RomanNumeralScore({config, value = {}, onChange, mode, feedback,
  authoring = false, disabled = false, onAnchors, onToggle, scrollRef}) {
  const hostRef = useRef(null), scoreRef = useRef(null);
  const callbacks = useRef({onAnchors});
  callbacks.current = {onAnchors};
  const [anchors, setAnchors] = useState([]), [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const source = config.musicXML;
  const chordSelect = config.answerMode === 'chord-select';
  const width = Math.max(600, Number(config.scoreWidth) || 900);
  const [renderedWidth, setRenderedWidth] = useState(width);
  useEffect(() => {
    const host = hostRef.current, target = scoreRef.current;
    if (!source || !host || !target) {setAnchors([]); return;}
    let disposed = false;
    setLoading(true); setError(''); setAnchors([]); target.replaceChildren();
    setRenderedWidth(width);
    const osmd = new OpenSheetMusicDisplay(target, {backend: 'svg', autoResize: false,
      renderSingleHorizontalStaffline: chordSelect, newSystemFromXML:false, newPageFromXML:false,
      drawTitle: false, drawSubtitle: false, drawComposer: false,
      drawPartNames: false, drawLyricist: false, drawMeasureNumbers: true});
    osmd.EngravingRules.MinimumDistanceBetweenSystems = 16;
    osmd.EngravingRules.MinNoteDistance = 8;
    osmd.EngravingRules.VoiceSpacingAddendVexflow = 50;
    // Suppress printed analysis from the XML; it is the student's task.
    osmd.EngravingRules.RenderChordSymbols = false;
    osmd.EngravingRules.RenderLyrics = false;
    async function load() {
      try {
        // Accept existing uploaded URLs and inline XML for hand-authored configs.
        await osmd.load(source);
        if (disposed) return;
        osmd.render();
        if (chordSelect) {
          const svgWidth = Math.max(0,...Array.from(target.querySelectorAll('svg')).map(svg => svg.getBoundingClientRect().width));
          setRenderedWidth(Math.max(width,Math.ceil(svgWidth)+48));
        }
        const next = collectRNAnchors(osmd, host);
        if (!next.length) throw new Error('No pitched note positions found in this score.');
        setAnchors(next); callbacks.current.onAnchors?.(next, source);
      } catch (err) {
        if (!disposed) setError(`Unable to display RN score: ${err.message}`);
      } finally {if (!disposed) setLoading(false);}
    }
    load();
    return () => {disposed = true; target.replaceChildren();};
  }, [source, width, chordSelect]);
  const selected = new Set((config.prompts || []).map(p => p.id));
  const byId = new Map(anchors.map(a => [a.id, a]));
  const locked = disabled || mode === 'review';
  const missing = !loading && !error && anchors.length > 0 && (config.prompts || []).some(p => !byId.has(p.id));
  return <>
    {loading && <p role="status">Loading score…</p>}
    {error && <p role="alert">{error}</p>}
    {!source && <p>Upload a MusicXML score to place {chordSelect ? 'chord menus' : 'Roman numeral prompts'}.</p>}
    {missing && <p role="alert">Some {chordSelect ? 'chord menus' : 'RN prompts'} no longer match this score. Reopen prompt placement in the editor.</p>}
    <div ref={scrollRef} tabIndex={0} aria-label="Scrollable music score" style={{overflowX:'auto', maxWidth:'100%',width:'100%'}}>
      {config.scaleLabel && <div data-rn-key-label style={{fontWeight:700, fontSize:16, color:'#111', marginBottom:8}}>
        {config.scaleLabel}
      </div>}
      <div className="rn-score" ref={hostRef} style={{width:chordSelect ? renderedWidth : width, paddingTop:chordSelect ? 76 : 0,paddingBottom:chordSelect ? 16 : 90}}>
        <div ref={scoreRef} style={{width}} />
        {rnCadenceRegions(config.cadenceQuestions || [], anchors).map(box =>
          <div key={box.id} className="rn-cadence-region" aria-label={`${box.label} cadence region`}
            style={{left:box.left,top:box.top,width:box.width,height:box.height}}>
            <span>{box.label}</span>
          </div>)}
        {authoring && anchors.flatMap(anchor => anchor.hits.map((hit, i) =>
          <button type="button" key={`${anchor.id}-${i}`} className="rn-candidate"
            style={{left:hit.x, top:hit.y}} disabled={disabled}
            aria-label={`Toggle RN at measure ${anchor.measure}, position ${anchor.time}`}
            aria-pressed={selected.has(anchor.id)} onClick={() => onToggle?.(anchor)}>
            {selected.has(anchor.id) ? '−' : '+'}
          </button>))}
        {(config.prompts || []).map((prompt, i) => {
          const anchor = byId.get(prompt.id);
          if (!anchor) return null;
          const part = feedback?.parts?.[prompt.id];
          const expected = Array.isArray(part?.expected) ? part.expected.join(' or ') : part?.expected;
          return <div className="rn-field" key={prompt.id} style={{left:anchor.x, top:chordSelect ? anchor.aboveY : anchor.y}}>
            {chordSelect && authoring ? <span aria-label={`Accepted chords for blank ${i+1}`}>{(Array.isArray(value?.rn?.[prompt.id]) ? value.rn[prompt.id] : [value?.rn?.[prompt.id]]).filter(Boolean).join(' / ') || 'Set key below'}</span> : chordSelect ? <ChordSymbolSelect aria-label={`${authoring ? 'Answer key' : 'Chord symbol'} ${i+1}, measure ${anchor.measure}`}
              value={value?.rn?.[prompt.id] ?? ''} locked={locked}
              aria-invalid={mode === 'review' && part?.correct === false}
              onChange={text => onChange?.({...value,rn:{...(value?.rn || {}), [prompt.id]:text}})} /> : <RomanNumeralInput aria-label={`${authoring ? 'Answer key' : 'Roman numeral'} ${i+1}, measure ${anchor.measure}`}
              value={value?.rn?.[prompt.id] ?? ''} readOnly={locked}
              aria-invalid={mode === 'review' && part?.correct === false}
              onChange={text => onChange?.({...value,rn:{...(value?.rn || {}), [prompt.id]:text}})} />}
            {mode === 'review' && part && <small>{part.correct ? '✓' : <>Expected: <span className={chordSelect ? undefined : 'rn-font'}>{chordSelect ? expected : encodeRomanNumeralText(expected)}</span></>}</small>}
          </div>;
        })}
      </div>
    </div>
  </>;
}
export const RNInputGuide = () => <p className="rn-guide">Type the Roman numeral and figures together: I, ii6, V64, V65, V43, V42, vii/o7, viio7, V7/V. Figures stack automatically. Case matters. Use o for diminished, /o for half-diminished, b for flat and # for sharp.</p>;
export default function RomanNumeralAnalysis({config, value, onChange, mode, feedback, hideFB}) {
  const scrollRef = useRef(null);
  return <QuestionLayout number={config.number} prompt={config.prompt} points={config.points}
    mode={mode} feedback={feedback} hideFB={hideFB} horizontalScrollRef={scrollRef}>
    <RomanNumeralScore config={config} value={value} onChange={onChange} mode={mode} feedback={feedback} scrollRef={scrollRef}/>
    <RNAnalysisQuestions config={config} value={value} onChange={onChange} mode={mode} feedback={feedback}/>
  </QuestionLayout>;
}
